import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { BrandMark } from "./ui.js";

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const hasOpenedMobileNav = useRef(false);

  useEffect(() => {
    if (mobileOpen) {
      hasOpenedMobileNav.current = true;
      workspaceRef.current?.setAttribute("inert", "");
      closeButtonRef.current?.focus();
      const closeOnEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") setMobileOpen(false);
      };
      window.addEventListener("keydown", closeOnEscape);
      return () => window.removeEventListener("keydown", closeOnEscape);
    }
    workspaceRef.current?.removeAttribute("inert");
    if (hasOpenedMobileNav.current) menuButtonRef.current?.focus();
  }, [mobileOpen]);

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 840px)");
    const closeWhenDesktop = (event: MediaQueryListEvent) => {
      if (!event.matches) setMobileOpen(false);
    };
    mobileQuery.addEventListener("change", closeWhenDesktop);
    return () => mobileQuery.removeEventListener("change", closeWhenDesktop);
  }, []);

  if (loading) return <div className="app-loading"><span className="spinner-ring" />Loading your workspace…</div>;
  if (!user) return <>{children}</>;

  const navItems = [
    { to: "/dashboard", label: "Overview", short: "OV" },
    { to: "/properties", label: "Properties", short: "PR" },
    { to: "/finance", label: "Finance", short: "FI" },
    { to: "/tax", label: "Tax", short: "TX" },
    { to: "/insights", label: "Insights", short: "IN" },
    { to: "/billing", label: "Billing", short: "BI" },
    { to: "/integrations", label: "Integrations", short: "IT" },
    { to: "/settings", label: "Settings", short: "SE" },
  ];

  return (
    <div className="app-shell">
      <aside id="primary-navigation" className={`app-sidebar${mobileOpen ? " mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <BrandMark />
          <button ref={closeButtonRef} className="mobile-close" type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation">×</button>
        </div>
        <nav className="app-nav" aria-label="Main navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
            >
              <span aria-hidden="true">{item.short}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <p>Signed in as</p>
          <strong>{user.email}</strong>
          <button
            className="btn btn-quiet"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            Log out
          </button>
        </div>
      </aside>
      {mobileOpen && <button className="sidebar-scrim" type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <div ref={workspaceRef} className="app-workspace" aria-hidden={mobileOpen ? true : undefined}>
        <header className="app-header">
          <button
            ref={menuButtonRef}
            className="menu-button"
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            aria-controls="primary-navigation"
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
          </button>
          <div className="mobile-header-brand"><BrandMark compact /></div>
          <div className="app-user">
            <span className="user-avatar" aria-hidden="true">{user.email.slice(0, 1).toUpperCase()}</span>
          </div>
        </header>
        <main className="app-main">{children}</main>
      </div>
    </div>
  );
}
